import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconCamera } from '@tabler/icons-react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useMyPhoto } from '@/lib/photo';
import { photoRules, useCopy } from '@/lib/settings';
import { NudgeCard } from '@/components/member/NudgeCard';
import { PersonAvatar } from './PersonAvatar';

const KEY = 'rides.photoNudge.dismissed';

function readDismissed(): boolean {
  try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
}

/** Whether Home should suggest a profile photo, and a way to hide the suggestion on this device. */
export function usePhotoNudge(isDriver?: boolean): { show: boolean; required: boolean; dismiss: () => void } {
  const { settings } = useCopy();
  const rules = photoRules(settings);
  const mine = useMyPhoto();
  const [dismissed, setDismissed] = useState(readDismissed);
  const required = !!isDriver && rules.driverRequired;
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(KEY, '1'); } catch { /* private mode: hide for this visit only */ }
  };
  // A photo a driver must have can't be hidden away.
  return { show: rules.enabled && !mine.isLoading && !mine.hasPhoto && (required || !dismissed), required, dismiss };
}

/**
 * Home nudge for members without a profile photo. Hiding it is remembered on this device (except
 * when drivers must have a photo). Drivers get the "drivers with a photo get booked more" line.
 */
export function PhotoNudgeCard({ isDriver, onDismiss }: { isDriver?: boolean; onDismiss?: () => void }) {
  const { t } = useCopy();
  const { getUserDisplayName } = useEnrichedAuth();
  const nudge = usePhotoNudge(isDriver);
  if (!nudge.show) return null;
  return (
    <NudgeCard
      tone={nudge.required ? 'warn' : 'info'}
      icon={<PersonAvatar name={getUserDisplayName()} size={44} decorative />}
      title="Add a profile photo"
      onDismiss={nudge.required ? undefined : onDismiss ?? nudge.dismiss}
      dismissLabel="Hide the photo suggestion"
      action={<Button component={Link} to="/account#photo" variant="default" leftSection={<IconCamera size={18} />}>Add a photo</Button>}
    >
      {isDriver ? (nudge.required ? t('photo.driver_required') : t('photo.driver_nudge')) : t('photo.intro')}
    </NudgeCard>
  );
}
